import React, { useState } from "react";
import { APP } from "@/config/app.config";
import { useNavigate, useLocation } from "react-router-dom";
import {
  User,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  Loader2,
  LayoutGrid,
} from "lucide-react";
import { childLoginApi, getMeApi, loginApi, mapApiRolesToAppRole } from "@/services/auth.service";
import { useAuthStore } from "@/store/auth.store";
import { useImpersonationStore } from "@/store/impersonation.store";
import { cn } from "@/lib/utils";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import { BackgroundOrbs } from "@/components/shared/ui/BackgroundOrbs";
import { ImageWithFallback } from "@/components/ui/ImageWithFallback";
import { useDetectWebView } from "@/hooks/useDetectWebView";
import type { UserRole } from "@/types";

const ROLE_DESTINATIONS: Record<UserRole, string> = {
  admin: "/admin/dashboard",
  teacher: "/teacher/dashboard",
  student: "/student/dashboard",
  family: "/parent/dashboard",
  guest: "/login",
};

// Préfixes de routes accessibles par rôle (cf. `RequireRole` dans le routeur).
// Sert à ne pas renvoyer l'utilisateur vers un `from` que son rôle ne peut pas
// atteindre — sinon `RequireRole` le rejette aussitôt vers /login (boucle).
const ROLE_PATH_PREFIXES: Record<UserRole, string[]> = {
  admin: ["/admin"],
  teacher: ["/teacher"],
  family: ["/parent"],
  student: ["/student"],
  guest: [],
};

function canRoleAccessPath(role: UserRole, path: string): boolean {
  return ROLE_PATH_PREFIXES[role].some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

export function LoginPage() {
  const isWebView = useDetectWebView();
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);

  const verifiedEmail = (location.state as { verifiedEmail?: string })?.verifiedEmail;

  const [identifier, setIdentifier] = useState(verifiedEmail || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Real login ─────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // 1. Authenticate → get token (route on identifier shape)
      const isEmail = identifier.includes("@");
      const { access_token, must_change_password } = isEmail
        ? await loginApi(identifier, password)
        : await childLoginApi(identifier, password);

      // 2. Fetch user profile with roles
      const { user: me } = await getMeApi(access_token);

      // 3. Map API roles → app role
      const role = mapApiRolesToAppRole(me.roles);

      // 4. Connexion interactive réelle : purger toute session d'impersonation
      // résiduelle (ex. `_imp`/impersonation-storage restaurés par le navigateur)
      // pour que la nouvelle session soit persistée normalement (localStorage) et
      // que la bannière d'impersonation ne s'affiche pas à tort.
      sessionStorage.removeItem("_imp");
      useImpersonationStore.getState().stop();

      // 5. Persist in store (localStorage via zustand/persist)
      login(role, access_token, me);

      // 6. Redirect: force password change or navigate to destination
      if (must_change_password) {
        localStorage.setItem(STORAGE_KEYS.MUST_CHANGE_PASSWORD, "true");
        navigate("/change-password");
      } else {
        localStorage.removeItem(STORAGE_KEYS.MUST_CHANGE_PASSWORD);
        const from = (location.state as { from?: string })?.from;
        // N'honorer `from` que si le rôle connecté peut réellement y accéder.
        // Sinon on retombe sur le tableau de bord du rôle (évite la boucle
        // login → route interdite → login lors d'un retour d'impersonation).
        const target = from && canRoleAccessPath(role, from) ? from : ROLE_DESTINATIONS[role];
        navigate(target);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erreur inconnue";
      // `fetch` rejette avec un TypeError (ex: "Failed to fetch") quand la requête n'aboutit
      // jamais à une réponse HTTP — coupure réseau ou API indisponible (souvent pendant un
      // déploiement), ce que Chrome rapporte à tort comme un blocage CORS dans la console.
      if (err instanceof TypeError || message.startsWith("5")) {
        setError("Le serveur est temporairement indisponible. Réessayez dans un moment.");
      } else if (
        message.includes("401") ||
        message.includes("422") ||
        message.toLowerCase().includes("invalid")
      ) {
        setError("Identifiant ou mot de passe incorrect.");
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4 md:p-10 font-sans overflow-hidden relative">
      <BackgroundOrbs />

      <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-24 items-center relative z-10">
        {/* Left Side: Login Form */}
        <div className="bg-white rounded-2xl p-8 md:p-16 shadow-2xl shadow-gray-200/50 border border-gray-100">
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-center">
                <img src="/favicon.svg" alt={APP.name} />
              </div>
              <span className="text-xl font-black text-[#003366] tracking-tighter uppercase italic">
                {APP.name}
              </span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 rounded-full text-indigo-600 text-[10px] font-black uppercase tracking-widest mb-6">
              <Sparkles size={14} />
              <span>Connectez-vous à votre espace</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-[#003366] leading-[1.1] tracking-tight mb-4">
              Bon retour sur <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-fuchsia-600 italic">
                {APP.name} !
              </span>
            </h1>
            <p className="text-gray-400 font-medium text-sm">
              Saisissez vos identifiants pour accéder à votre tableau de bord.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                Identifiant
              </label>
              <div className="relative group">
                <User
                  className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-500 transition-colors"
                  size={20}
                />
                <input
                  type="text"
                  placeholder="Email ou identifiant élève"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  disabled={isLoading}
                  autoComplete="username"
                  className="w-full bg-gray-50 border border-gray-100 rounded-xl pl-14 pr-6 py-4 outline-none focus:ring-4 focus:ring-indigo-50 focus:bg-white transition-all text-gray-900 font-bold disabled:opacity-50"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center px-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Mot de passe
                </label>
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:underline"
                >
                  Oublié ?
                </button>
              </div>
              <div className="relative group">
                <Lock
                  className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-500 transition-colors"
                  size={20}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  className="w-full bg-gray-50 border border-gray-100 rounded-xl pl-14 pr-14 py-4 outline-none focus:ring-4 focus:ring-indigo-50 focus:bg-white transition-all text-gray-900 font-bold disabled:opacity-50"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Verified email banner */}
            {verifiedEmail && !error && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 text-sm font-bold text-emerald-600">
                Votre email a été vérifié avec succès. Connectez-vous maintenant.
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm font-bold text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#003366] text-white rounded-xl py-5 font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-blue-900/20 hover:bg-black transition-all active:scale-[0.98] flex items-center justify-center gap-3 group disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Connexion en cours…
                </>
              ) : (
                <>
                  Connexion sécurisée
                  <ArrowRight
                    className="group-hover:translate-x-1 transition-transform"
                    size={18}
                  />
                </>
              )}
            </button>

            {!isWebView && (
              <div className="relative mt-8 pt-8 border-t border-gray-100">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-[10px] font-black text-gray-300 uppercase tracking-widest">
                  Ou
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/parent/catalog")}
                  className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl bg-gray-50 border border-gray-100 text-[#003366] font-black text-[10px] uppercase tracking-widest hover:bg-indigo-50 hover:border-indigo-100 hover:text-indigo-600 transition-all group"
                >
                  <LayoutGrid
                    size={16}
                    className="text-gray-400 group-hover:text-indigo-600 transition-colors"
                  />
                  Visiter le catalogue
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Right Side: High-end Collage */}
        <div className="relative hidden lg:grid grid-cols-2 gap-6">
          <div className="aspect-square rounded-xl overflow-hidden bg-[#4CC9F0] border-[8px] border-white shadow-2xl rotate-[-3deg] relative z-10 hover:rotate-0 transition-transform duration-700">
            <ImageWithFallback
              src="/2.png"
              alt="Apprentissage"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="aspect-square rounded-xl overflow-hidden bg-[#F59E0B] border-[8px] border-white shadow-2xl translate-y-12 rotate-[5deg] hover:rotate-0 transition-transform duration-700">
            <ImageWithFallback
              src="/3.png"
              alt="Éducation"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="aspect-square rounded-xl overflow-hidden bg-[#10B981] border-[8px] border-white shadow-2xl -translate-y-6 rotate-[2deg] hover:rotate-0 transition-transform duration-700">
            <ImageWithFallback src="/5.png" alt="Élève" className="w-full h-full object-cover" />
          </div>

          <div className="aspect-square rounded-xl overflow-hidden bg-[#D946EF] border-[8px] border-white shadow-2xl translate-y-6 rotate-[-4deg] hover:rotate-0 transition-transform duration-700">
            <ImageWithFallback src="/6.png" alt="Réussite" className="w-full h-full object-cover" />
          </div>

          {/* Badge overlays */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white rounded-full shadow-2xl border-4 border-indigo-50 z-20 flex items-center justify-center animate-bounce">
            <CheckCircle2 size={36} className="text-emerald-500" />
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] font-black text-gray-300 uppercase tracking-[0.3em]">
        © {APP.tagline} 2026
      </div>
    </div>
  );
}
